import dayjs from "dayjs"
import { useMemo } from "react"
import { useQuery } from "urql"

import type {
  FieldGroupEnum,
  GetFileInfoQuery,
  GetFileInfoQueryVariables,
  ReferenceTableName,
  SignedUrl,
} from "@/graphql/codegen/graphql"
import { GetFileInfoDocument } from "@/graphql/codegen/graphql"

interface BaseProps {
  referenceId?: string
  referenceIds?: string[]
  fieldGroups?: FieldGroupEnum[]
  referenceTableName: ReferenceTableName
  pause?: boolean
}

type UseGetFileUploadDataProps = BaseProps &
  (BaseProps["pause"] extends false ? Required<Omit<BaseProps, "pause">> : Partial<Omit<BaseProps, "pause">>)

interface UseGetFileUploadDataReturn {
  fileList: SignedUrl[]
  refetchFileUploadData: (immediate?: boolean) => void
  fileFetching: boolean
}

export const useGetFileUploadData = ({
  referenceId,
  referenceIds,
  fieldGroups,
  referenceTableName,
  pause,
}: UseGetFileUploadDataProps): UseGetFileUploadDataReturn => {
  const hasRequiredFields = referenceId || !!referenceIds?.length || !!fieldGroups?.length || pause
  if (!hasRequiredFields) {
    throw new Error("Either referenceId or referenceIds or fieldGroups must be provided.")
  }
  const [{ data: fileUploadData, fetching: fileFetching }, refetchData] = useQuery<
    GetFileInfoQuery,
    GetFileInfoQueryVariables
  >({
    query: GetFileInfoDocument,
    variables: {
      fileInfoInput: {
        referenceTableName,
        ...(referenceId && { referenceId }),
        ...(referenceIds && { referenceIds }),
        fieldGroups,
      },
    },
    pause,
  })

  const fileList = useMemo(() => {
    if (!fileUploadData) return []

    if (fileUploadData.fileUpload.getFileInfo.__typename === "GetFileInfoFailure") {
      return []
    }

    return fileUploadData.fileUpload.getFileInfo.signedUrls.sort((a, b) => {
      const aDate = dayjs(a.file?.createdOn)
      const bDate = dayjs(b.file?.createdOn)
      return bDate.diff(aDate) // Sort descending (newest first)
    })
  }, [fileUploadData])

  const refetchFileUploadData = (immediate?: boolean) => {
    if (immediate) {
      refetchData({ requestPolicy: "network-only" })
      return
    }

    // Default delay gives a just-uploaded file time to become queryable; a delete
    // has no such lag, so callers reflecting a deletion should pass `immediate: true`.
    setTimeout(() => {
      refetchData({ requestPolicy: "network-only" })
    }, 2000)
  }

  return {
    fileList,
    refetchFileUploadData,
    fileFetching,
  }
}
